import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { interval, Subject, takeUntil } from 'rxjs';
import { CompanyTypeEnum } from 'src/app/models/company';

import { resetHubspotCookiesAndReloadPage } from '../../../../../../lib/hubspot';
import { AuthService } from '../../../../../auth/auth.service';
import { EPP_REQUESTS_PAGE_NAME } from '../../../../../early-pay-requests/early-pay-requests.service';
import { User } from '../../../../../models/user';
import { PREQUAL_PAGE_NAME } from '../../../../../prequal/prequal.service';
import { LogoComponent } from '../../../../../ui-kit/logo/logo.component';
import { PermissionDirective } from '../../../../directives/permission.directive';
import { UserService } from '../../../../services/user.service';
import { getUserHomePage } from '../../../../utils';
import { HeaderAccountPanelComponent } from '../header-account-panel/header-account-panel.component';
import { MenuItem, HeaderMenuItemComponent } from '../header-menu-item/header-menu-item.component';

@Component({
  selector: 'app-user-header',
  templateUrl: './user-header.component.html',
  styleUrls: ['./user-header.component.scss'],
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    RouterLink,
    HeaderMenuItemComponent,
    HeaderAccountPanelComponent,
    PermissionDirective,
    RouterLinkActive,
    LogoComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserHeaderComponent implements OnChanges, AfterViewInit {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  @Input() user: User;
  @ViewChild('desktopMenu') textMenuItems: ElementRef;

  companyType: CompanyTypeEnum = null;
  isGC = false;
  homePage = '/';
  menuItems: MenuItem[] = [];
  showScrollButtons = false;

  private unsubscribe$ = new Subject<void>();

  @HostListener('window:resize')
  onResize(): void {
    this.updateScrollButtonVisibility();
  }

  ngAfterViewInit(): void {
    this.updateScrollButtonVisibility();
  }

  ngOnChanges(): void {
    const visitedPages = this.userService.getUser().visited_pages;
    const enableEppSubscriptionTooltip = !visitedPages.includes('epp-subscription-tooltip');

    this.companyType = this.user?.company_type;
    this.isGC = this.user?.is_company_gc;
    this.homePage = getUserHomePage(this.user);
    this.menuItems = [
      {
        name: 'Home',
        link: '/homepage',
        permission: null,
        condition: true,
        hasBadge: false,
      },
      {
        name: 'Dashboard',
        link: '/dashboard',
        permission: 'constrafor.can_view_dashboard_tab',
        condition: this.isGC && this.companyType === 'contractor',
        hasBadge: false,
      },
      {
        name: 'Financials',
        link: '/financials',
        permission: 'constrafor.can_view_financials_tab',
        condition: this.companyType === 'contractor' && !this.isGC,
        hasBadge: false,
      },
      {
        name: 'Early Pay',
        link: '/early-pay',
        permission: ['epp.view_epp_requests_sub', 'epp.view_epp_requests', 'constrafor.can_view_epp_tab'],
        condition: true,
        hasBadge: !this.user?.visited_pages.includes(EPP_REQUESTS_PAGE_NAME),
        tooltipEnabled: enableEppSubscriptionTooltip,
        tooltipTitle: 'Save money with EPP subscription',
        tooltipText:
          'Check out our plans to help you save money for every EPP that you submit. Click here to see our new plans',
      },
      {
        name: 'Prequal',
        link: '/prequal',
        permission: 'constrafor.can_view_prequal_tab',
        condition: this.companyType === 'contractor',
        hasBadge: !this.user?.visited_pages.includes(PREQUAL_PAGE_NAME),
      },
      {
        name: 'Projects',
        link: '/projects',
        permission: 'constrafor.can_view_project_tab',
        condition: true,
        hasBadge: false,
      },
      {
        name: 'Contracts',
        link: '/contracts',
        permission: 'constrafor.can_view_contract_tab',
        condition: true,
        hasBadge: false,
      },
      {
        name: 'COIs',
        link: '/certificates-of-insurance',
        permission: 'constrafor.can_view_coi_tab',
        condition: true,
        hasBadge: false,
      },
      {
        name: 'Insurance',
        link: '/subcontractor-insurance',
        permission: 'constrafor.can_view_insurance_tab',
        condition: true,
        hasBadge: false,
      },
      {
        name: 'Invoicing',
        link: '/invoicing',
        permission: 'constrafor.can_view_invoice_tab',
        condition: true,
        hasBadge: false,
      },
      {
        name: 'Diversity',
        link: '/diversity',
        permission: 'constrafor.can_view_diversity_tab',
        condition: true,
        hasBadge: false,
      },
      {
        name: 'Diversity',
        link: '/diversity-tracking',
        permission: 'constrafor.can_view_diversity_tracking_tab',
        condition: true,
        hasBadge: false,
      },
      {
        name: 'Contractors',
        link: '/contractors',
        permission: null,
        condition: this.companyType !== 'lender',
        hasBadge: false,
      },
      {
        name: 'Lending',
        link: '/lender-portal',
        permission: 'constrafor.can_view_lending_tab',
        condition: true,
        hasBadge: false,
      },
    ];
  }

  private updateScrollButtonVisibility(): void {
    if (this.textMenuItems?.nativeElement) {
      const el = this.textMenuItems.nativeElement;
      const show = el.scrollWidth - el.clientWidth > 25;
      if (show !== this.showScrollButtons) {
        this.showScrollButtons = show;
        this.cdr.markForCheck();
      }
    }
  }

  stopScroll(): void {
    this.unsubscribe$.next();
  }

  startMenuScrolling(direction: 'right' | 'left'): void {
    let step = 10;
    let stepInc = 1;
    if (direction === 'left') {
      step *= -1;
      stepInc *= -1;
    }
    interval(30)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (Math.abs(step) < 30) {
          step += stepInc;
        }
        this.textMenuItems.nativeElement.scrollLeft += step;
      });
  }

  markTooltipAsVisited(itemName: string): void {
    if (itemName === 'Early Pay') {
      this.userService
        .checkAndMarkPageAsVisited('epp-subscription-tooltip')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  logout(): void {
    this.authService.logout().subscribe(() => {
      resetHubspotCookiesAndReloadPage();
    });
  }
}
